import { useGetTicketTypesMutation } from '@services/member/list/list.endpoints';
import { TicketType } from '@services/member/list/ticket-types/ticket-types.types';
import { useGetMemberMutation } from '@services/member/member/member.endpoints';
import { Member } from '@services/member/member/member.types';
import {
    useCreateTicketingsMutation
} from '@services/member/ticketing/ticketing.endpoints';
import { TicketingToCreate } from '@services/member/ticketing/ticketing.types';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface TicketBlock {
    id: string;
    ticketType: TicketType | null;
    firstTicketNumber: number;
    lastTicketNumber: number;
    amount: number;
    observation: string;
}

export const useAddMultiPageVM = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const memberId = searchParams.get('memberId');
    const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
    const [member, setMember] = useState<Member>();
    const [ticketBlocks, setTicketBlocks] = useState<TicketBlock[]>([
        {
            id: '1',
            ticketType: null,
            firstTicketNumber: 0,
            lastTicketNumber: 0,
            amount: 0,
            observation: ''
        }
    ]);

    const [getTicketTypes, { isSuccess: isGetTicketTypesSuccess, isLoading: isGetTicketTypesLoading, data: ticketTypesData }] =
        useGetTicketTypesMutation();

    const [
        getMember,
        {
            isLoading: isGetMemberLoading,
            isError: isGetMemberError,
            isSuccess: isGetMemberSuccess,
            error: getMemberError,
            data: getMemberResponse
        }
    ] = useGetMemberMutation();


    const [
        createTicketings,
        {
            isLoading: isCreateTicketingsLoading,
            isError: isCreateTicketingsError,
            isSuccess: isCreateTicketingsSuccess,
            error: createTicketingsError,
            data: createTicketingsResponse
        }
    ] = useCreateTicketingsMutation();


    useEffect(() => {
        if (!memberId) return;
        getMember({ id: memberId });
    }, [memberId]);

    useEffect(() => {
        getTicketTypes(undefined);
    }, [getTicketTypes]);

    useEffect(() => {
        if (isGetTicketTypesLoading) return;
        if (isGetTicketTypesSuccess && ticketTypesData) {
            const sortedTicketTypes = ticketTypesData.data
                .slice()
                .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
            setTicketTypes(sortedTicketTypes);
        }
    }, [isGetTicketTypesSuccess, isGetTicketTypesLoading, ticketTypesData]);

    useEffect(() => {
        if (isGetMemberLoading) return;
        if (isGetMemberError && getMemberError) return;
        if (isGetMemberSuccess && getMemberResponse) {
            setMember(getMemberResponse.data);
        }
    }, [isGetMemberError, isGetMemberSuccess, getMemberError, getMemberResponse, isGetMemberLoading]);

    useEffect(() => {
        if (isCreateTicketingsLoading) return;
        if (isCreateTicketingsError) return;
        if (createTicketingsError) return;

        if (isCreateTicketingsSuccess && createTicketingsResponse) {
            if (memberId) {
                navigate(`/members-management/members/${memberId}/details/tickets`);
            } else {
                navigate(`/members-management/ticketings`);
            }
        }
    }, [
        isCreateTicketingsError,
        isCreateTicketingsSuccess,
        createTicketingsError,
        createTicketingsResponse,
        isCreateTicketingsLoading,
        navigate
    ]);

    const addTicketBlock = () => {
        const newBlock: TicketBlock = {
            id: Date.now().toString(),
            ticketType: null,
            firstTicketNumber: 0,
            lastTicketNumber: 0,
            amount: 0,
            observation: ''
        };
        setTicketBlocks([newBlock, ...ticketBlocks]);
    };

    const removeTicketBlock = (blockId: string) => {
        if (ticketBlocks.length > 1) {
            setTicketBlocks(ticketBlocks.filter(block => block.id !== blockId));
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateTicketBlock = (blockId: string, field: keyof TicketBlock, value: any) => {
        setTicketBlocks(prevBlocks => 
            prevBlocks.map(block => 
                block.id === blockId ? { ...block, [field]: value } : block
            )
        );
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const submitForm = async (values: { member: any; ticketingAt: string }) => {
        // Créer un tableau de TicketingToCreate à partir des blocs
        const ticketingArray: TicketingToCreate[] = ticketBlocks.map(block => ({
            member: values.member,
            ticketType: block.ticketType as TicketType, // Utiliser le type de billet du bloc
            ticketingAt: values.ticketingAt,
            saleAmount: 0, // À calculer selon vos besoins
            saleCount: Number(block.lastTicketNumber) - Number(block.firstTicketNumber) + 1,
            observation: block.observation,
            firstTicketNumber: block.firstTicketNumber,
            lastTicketNumber: block.lastTicketNumber,
            amount: block.amount,
            isArchived: false
        }));

        await createTicketings(ticketingArray); // API pas encore prête        
    };

    return {
        submitForm,
        isLoading: isCreateTicketingsLoading,
        ticketTypes,
        member,
        ticketBlocks,
        addTicketBlock,
        removeTicketBlock,
        updateTicketBlock
    };
};
